import { sql, relations } from "drizzle-orm";
import { pgTable, text, varchar, timestamp, decimal, integer, pgEnum, boolean, jsonb, unique, bigint, date, inet } from "drizzle-orm/pg-core";
import { createInsertSchema } from "drizzle-zod";
import { z } from "zod";

// ============================================
// Country & Currency Types
// ============================================

export type BaseCountry = 'AU' | 'NZ' | 'IN' | 'GB' | 'US';
export type BaseCurrency = 'AUD' | 'NZD' | 'INR' | 'GBP' | 'EUR' | 'USD';

// ============================================
// Enums
// ============================================

export const userTypeEnum = pgEnum("user_type", ["ADMIN", "CUSTOMER"]);
export const roleEnum = pgEnum("role", ["SUPER", "ORGANIZATION", "BUSINESS_UNIT", "FINANCE"]);
export const statusEnum = pgEnum("status", ["ACTIVE", "INACTIVE", "SUSPENDED", "PENDING"]);
export const requestStatusEnum = pgEnum("request_status", ["DRAFT", "PENDING", "APPROVED", "REJECTED"]);
export const kycStatusEnum = pgEnum("kyc_status", ["NOT_STARTED", "IN_PROGRESS", "PENDING_REVIEW", "VERIFIED", "REJECTED", "EXPIRED"]);
export const cardStatusEnum = pgEnum("card_status", ["PENDING_APPROVAL", "ACTIVE", "FROZEN", "CANCELLED", "EXPIRED"]);
export const cardTypeEnum = pgEnum("card_type", ["VIRTUAL"]);
export const cardNetworkEnum = pgEnum("card_network", ["VISA", "MASTERCARD", "RUPAY"]);
export const txnTypeEnum = pgEnum("txn_type", ["AUTHORIZATION", "CAPTURE", "REFUND", "REVERSAL", "LOAD", "WITHDRAWAL"]);
export const txnStatusEnum = pgEnum("txn_status", ["PENDING", "AUTHORIZED", "SETTLED", "DECLINED", "REVERSED", "REFUNDED"]);
export const walletStatusEnum = pgEnum("wallet_status", ["ACTIVE", "FROZEN"]);
export const shippingStatusEnum = pgEnum("shipping_status", ["PENDING", "SHIPPED", "DELIVERED"]);
export const ticketStatusEnum = pgEnum("ticket_status", ["OPEN", "IN_PROGRESS", "RESOLVED", "CLOSED"]);
export const ticketPriorityEnum = pgEnum("ticket_priority", ["LOW", "MEDIUM", "HIGH", "URGENT"]);
export const ticketCategoryEnum = pgEnum("ticket_category", ["CARD_ISSUE", "TRANSACTION", "KYC", "BILLING", "OTHER"]);
export const integrationProviderEnum = pgEnum("integration_provider", [
  "AIRWALLEX", "NIUM", "M2P", "YAP", "ZETA", "MARQETA",  // Card issuers (AU/NZ + Global)
  "ISERVEU", "HOST",                                      // India banking/card issuer (Host Server)
  "PAYHUB",                                               // India payment gateway/QR
  "FRANKIE", "GREENID", "ONFIDO", "JUMIO",               // KYC providers (AU/NZ)
  "AADHAAR_UIDAI",                                        // India KYC (Aadhaar)
  "TWILIO", "MSG91"                                       // SMS providers
]);
export const integrationTypeEnum = pgEnum("integration_type", ["CARD_ISSUER", "KYC_PROVIDER", "SMS_PROVIDER", "PAYMENT_GATEWAY", "BANKING_PARTNER"]);
export const environmentEnum = pgEnum("environment", ["sandbox", "production"]);

// India-specific enums
export const aadhaarKycStatusEnum = pgEnum("aadhaar_kyc_status", [
  "NOT_STARTED",
  "OTP_SENT",
  "OTP_VERIFIED",
  "BIOMETRIC_PENDING",
  "BIOMETRIC_VERIFIED",
  "VERIFIED",
  "FAILED",
  "EXPIRED"
]);

export const upiPaymentStatusEnum = pgEnum("upi_payment_status", [
  "PENDING",
  "SCANNED",
  "PROCESSING",
  "SUCCESS",
  "FAILED",
  "EXPIRED",
  "REFUNDED"
]);

export const payhubEventTypeEnum = pgEnum("payhub_event_type", [
  "QR_GENERATED",
  "QR_SCANNED",
  "PAYMENT_INITIATED",
  "PAYMENT_SUCCESS",
  "PAYMENT_FAILED",
  "PAYMENT_PENDING",
  "REFUND_INITIATED",
  "REFUND_COMPLETED",
  "TOPUP_INITIATED",
  "TOPUP_SUCCESS",
  "TOPUP_FAILED"
]);

export const iserveuOperationTypeEnum = pgEnum("iserveu_operation_type", [
  "KYC_AADHAAR_INIT",
  "KYC_AADHAAR_OTP",
  "KYC_BIOMETRIC",
  "CARD_CREATE",
  "CARD_ACTIVATE",
  "CARD_BLOCK",
  "CARD_UNBLOCK",
  "WALLET_CREATE",
  "WALLET_CREDIT",
  "WALLET_DEBIT",
  "WITHDRAWAL"
]);

// Transaction journey status (for Display Panel)
export const journeyStatusEnum = pgEnum("journey_status", [
  "INITIATED",
  "READING",       // Fetching/processing
  "PENDING",       // Waiting for external response
  "SUCCESS",
  "FAILURE",
  "TIMEOUT",
  "CANCELLED"
]);

// ============================================
// Users (Unified Admins + Customers)
// ============================================

export const users = pgTable("users", {
  // Core Identity
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userType: userTypeEnum("user_type").notNull(),
  email: text("email").notNull().unique(),
  phone: varchar("phone", { length: 20 }),

  // Authentication (ADMIN only)
  passwordHash: text("password_hash"),

  // Cross-system reference: stores the Central API entity ID for sync resolution
  centralApiId: varchar("central_api_id", { length: 100 }).unique(),

  // Two-Factor Authentication (ADMIN only)
  twoFactorEnabled: boolean("two_factor_enabled").default(false).notNull(),
  twoFactorMethod: varchar("two_factor_method", { length: 10 }), // 'TOTP' | 'EMAIL'
  totpSecretEncrypted: text("totp_secret_encrypted"),
  twoFactorBackupCodes: text("two_factor_backup_codes").array(),

  // Password Management (ADMIN only)
  passwordResetRequired: boolean("password_reset_required").default(false).notNull(),
  passwordChangedAt: timestamp("password_changed_at"),

  // Customer portal PIN (CUSTOMER only) — second factor after SMS OTP
  customerPinHash: text("customer_pin_hash"),
  customerPinAttempts: integer("customer_pin_attempts").default(0).notNull(),
  customerPinLockedUntil: timestamp("customer_pin_locked_until"),

  // Personal Information
  firstName: text("first_name"),
  lastName: text("last_name"),
  dateOfBirth: date("date_of_birth"), // Primarily for CUSTOMER
  avatarUrl: text("avatar_url"),

  // Organizational Scope
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }),

  // Authorization (ADMIN only)
  role: roleEnum("role"), // Required for ADMIN, null for CUSTOMER

  // Status & Activity
  status: statusEnum("status").default("PENDING").notNull(),
  lastLoginAt: timestamp("last_login_at"),

  // KYC & Compliance (CUSTOMER only)
  kycStatus: kycStatusEnum("kyc_status").default("NOT_STARTED").notNull(),
  kycTier: integer("kyc_tier").default(0).notNull(),
  kycVerifiedAt: timestamp("kyc_verified_at"),

  // India-specific KYC fields (CUSTOMER only)
  aadhaarNumberEncrypted: text("aadhaar_number_encrypted"),
  aadhaarVerified: boolean("aadhaar_verified").default(false).notNull(),
  aadhaarVerifiedAt: timestamp("aadhaar_verified_at"),
  panNumber: varchar("pan_number", { length: 10 }),
  upiVpa: varchar("upi_vpa", { length: 100 }),

  // Address (primarily CUSTOMER)
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: text("state"),
  postalCode: varchar("postal_code", { length: 20 }),
  country: varchar("country", { length: 2 }).default("AU"),

  // Workflow (CUSTOMER only)
  requestStatus: requestStatusEnum("request_status"),

  // Timestamps
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const usersRelations = relations(users, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [users.organizationId],
    references: [organizations.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [users.businessUnitId],
    references: [businessUnits.id],
  }),
  // Customer-specific relations
  wallets: many(wallets),
  cards: many(virtualCards),
  kycRecords: many(kycRecords),
  supportTickets: many(supportTickets),
}));

// ============================================
// Organizations (Top Level)
// ============================================

export const organizations = pgTable("organizations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  name: text("name").notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  email: text("email"),
  phone: varchar("phone", { length: 20 }),
  status: statusEnum("status").default("ACTIVE").notNull(),
  apiKey: text("api_key").notNull().unique(),

  // Settings (JSON for flexibility)
  settings: jsonb("settings").$type<{
    maxBusinessUnits?: number;
    maxCustomersPerOrganization?: number;
    baseCountry?: BaseCountry;
    baseCurrency?: BaseCurrency;
    features?: {
      virtualCards?: boolean;

      internationalTransactions?: boolean;
    };
    branding?: {
      logo?: string;
      primaryColor?: string;
      secondaryColor?: string;
    };
    lowBalanceAlertCents?: number;
    lowBalanceAlertEnabled?: boolean;
  }>(),

  // Organization Funding Pool (pre-funded balance for customer allocations)
  fundingBalanceCents: bigint("funding_balance_cents", { mode: "number" }).default(0).notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const organizationsRelations = relations(organizations, ({ many }) => ({
  businessUnits: many(businessUnits),
  products: many(products),
  admins: many(admins),
  integrations: many(integrations),
}));

// ============================================
// Products (Card Programs)
// ============================================

export const products = pgTable("products", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),

  name: text("name").notNull(),
  description: text("description"),
  cardType: cardTypeEnum("card_type").default("VIRTUAL").notNull(),
  cardNetwork: cardNetworkEnum("card_network").default("VISA").notNull(),

  currency: varchar("currency", { length: 3 }).default("INR"),

  // Default limits for this product (in cents/paise)
  defaultDailyLimitCents: bigint("default_daily_limit_cents", { mode: "number" }),
  defaultMonthlyLimitCents: bigint("default_monthly_limit_cents", { mode: "number" }),
  defaultSingleTxnLimitCents: bigint("default_single_txn_limit_cents", { mode: "number" }),
  maxBalanceCents: bigint("max_balance_cents", { mode: "number" }),
  minLoadAmountCents: bigint("min_load_amount_cents", { mode: "number" }),
  maxLoadAmountCents: bigint("max_load_amount_cents", { mode: "number" }),

  // iServeU portal fields
  bin: varchar("bin", { length: 20 }),
  binRange: varchar("bin_range", { length: 20 }),
  cardValidityYears: integer("card_validity_years"),
  kycType: varchar("kyc_type", { length: 30 }),
  category: varchar("category", { length: 50 }),
  velocityLimits: jsonb("velocity_limits").$type<Record<string, any>>(),
  iserveuSyncedAt: timestamp("iserveu_synced_at"),

  // Status
  status: statusEnum("status").default("ACTIVE").notNull(),
  requestStatus: requestStatusEnum("request_status").default("DRAFT").notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const productsRelations = relations(products, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [products.organizationId],
    references: [organizations.id],
  }),
  inventory: many(cardInventory),
  virtualCards: many(virtualCards),
}));

// ============================================
// Business Units
// ============================================

export const businessUnits = pgTable("business_units", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),

  name: text("name").notNull(),
  slug: varchar("slug", { length: 100 }).notNull().unique(),
  email: text("email"),
  phone: varchar("phone", { length: 20 }),
  status: statusEnum("status").default("ACTIVE").notNull(),

  // Card Program Settings
  cardProgramName: text("card_program_name"),
  issuerCode: varchar("issuer_code", { length: 50 }),
  binRange: varchar("bin_range", { length: 20 }),
  maxCardsPerCustomer: integer("max_cards_per_customer").default(5),

  // Limits (in cents)
  defaultDailyLimitCents: bigint("default_daily_limit_cents", { mode: "number" }),
  defaultMonthlyLimitCents: bigint("default_monthly_limit_cents", { mode: "number" }),

  // Billing
  platformFeePercent: decimal("platform_fee_percent", { precision: 5, scale: 2 }).default("0").notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const businessUnitsRelations = relations(businessUnits, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [businessUnits.organizationId],
    references: [organizations.id],
  }),
  customers: many(customers),
  admins: many(admins),
  inventory: many(cardInventory),
  supportTickets: many(supportTickets),
}));

// ============================================
// Admins (Operators/Users)
// ============================================

export const admins = pgTable("admins", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  email: text("email").notNull().unique(),
  password: text("password").notNull(),
  name: text("name").notNull(),
  role: roleEnum("role").notNull(),
  avatarUrl: text("avatar_url"),

  // Scope (null means platform level for SUPER)
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }),

  status: statusEnum("status").default("ACTIVE").notNull(),
  lastLoginAt: timestamp("last_login_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),

  // Password management
  passwordResetRequired: boolean("password_reset_required").default(false),
  passwordChangedAt: timestamp("password_changed_at"),

  // Two-factor authentication
  twoFactorEnabled: boolean("two_factor_enabled").default(false),
  twoFactorMethod: varchar("two_factor_method", { length: 10 }), // 'TOTP' or 'EMAIL'
  totpSecretEncrypted: text("totp_secret_encrypted"),
  twoFactorBackupCodes: text("two_factor_backup_codes").array(),
});

export const adminsRelations = relations(admins, ({ one }) => ({
  organization: one(organizations, {
    fields: [admins.organizationId],
    references: [organizations.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [admins.businessUnitId],
    references: [businessUnits.id],
  }),
}));

// ============================================
// Customers (Card Holders)
// ============================================

export const customers = pgTable("customers", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }).notNull(),

  // Personal Info
  firstName: text("first_name"),
  lastName: text("last_name"),
  email: text("email").notNull(),
  phone: varchar("phone", { length: 20 }).notNull(),
  dateOfBirth: date("date_of_birth"),
  avatarUrl: text("avatar_url"), // Customer portal profile picture

  // Address
  addressLine1: text("address_line1"),
  addressLine2: text("address_line2"),
  city: text("city"),
  state: varchar("state", { length: 50 }),
  postalCode: varchar("postal_code", { length: 20 }),
  country: varchar("country", { length: 2 }).default("AU"),

  // KYC
  kycStatus: kycStatusEnum("kyc_status").default("NOT_STARTED").notNull(),
  kycTier: integer("kyc_tier").default(0).notNull(),
  kycVerifiedAt: timestamp("kyc_verified_at"),

  // India-specific fields (Phase 1)
  aadhaarNumberEncrypted: text("aadhaar_number_encrypted"), // Encrypted 12-digit Aadhaar
  aadhaarVerified: boolean("aadhaar_verified").default(false),
  aadhaarVerifiedAt: timestamp("aadhaar_verified_at"),
  panNumber: varchar("pan_number", { length: 10 }), // 10-char PAN (optional)
  upiVpa: varchar("upi_vpa", { length: 100 }), // customer@upi format

  // Status
  status: statusEnum("status").default("PENDING").notNull(),
  requestStatus: requestStatusEnum("request_status").default("DRAFT").notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const customersRelations = relations(customers, ({ one, many }) => ({
  organization: one(organizations, {
    fields: [customers.organizationId],
    references: [organizations.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [customers.businessUnitId],
    references: [businessUnits.id],
  }),
  wallets: many(wallets),
  cards: many(virtualCards),
  kycRecords: many(kycRecords),
  supportTickets: many(supportTickets),
}));

// ============================================
// KYC Records
// ============================================

export const kycRecords = pgTable("kyc_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),

  // Document Info
  documentType: varchar("document_type", { length: 50 }), // AADHAAR, PAN, PASSPORT, DRIVERS_LICENSE
  documentNumberEncrypted: text("document_number_encrypted"),
  documentExpiry: date("document_expiry"),
  documentVerified: boolean("document_verified").default(false),

  // Biometric
  facialMatchScore: decimal("facial_match_score", { precision: 5, scale: 2 }),
  livenessPassed: boolean("liveness_passed").default(false),

  // Verification
  verificationProvider: varchar("verification_provider", { length: 50 }),
  externalVerificationId: varchar("external_verification_id", { length: 255 }),
  verificationResponse: jsonb("verification_response"),

  // India-specific: Aadhaar KYC fields
  aadhaarReferenceId: varchar("aadhaar_reference_id", { length: 100 }), // Host Server reference
  aadhaarOtpSentAt: timestamp("aadhaar_otp_sent_at"),
  aadhaarOtpVerifiedAt: timestamp("aadhaar_otp_verified_at"),
  aadhaarKycStatus: aadhaarKycStatusEnum("aadhaar_kyc_status").default("NOT_STARTED"),
  aadhaarXmlDataEncrypted: text("aadhaar_xml_data_encrypted"), // Encrypted UIDAI response
  aadhaarName: text("aadhaar_name"), // Name as per Aadhaar
  aadhaarDob: date("aadhaar_dob"), // DOB from Aadhaar
  aadhaarGender: varchar("aadhaar_gender", { length: 10 }),
  aadhaarAddress: text("aadhaar_address"), // Address from Aadhaar
  aadhaarPhoto: text("aadhaar_photo"), // Base64 photo from Aadhaar (if available)

  // Risk
  riskScore: integer("risk_score"),
  pepCheckResult: varchar("pep_check_result", { length: 20 }),
  sanctionsCheckResult: varchar("sanctions_check_result", { length: 20 }),

  verifiedBy: varchar("verified_by").references(() => admins.id),
  verifiedAt: timestamp("verified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const kycRecordsRelations = relations(kycRecords, ({ one }) => ({
  user: one(users, {
    fields: [kycRecords.userId],
    references: [users.id],
  }),
  verifiedByAdmin: one(admins, {
    fields: [kycRecords.verifiedBy],
    references: [admins.id],
  }),
}));

// ============================================
// Wallets
// ============================================

export const wallets = pgTable("wallets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),

  label: varchar("label", { length: 100 }),
  isDefault: boolean("is_default").default(true).notNull(),
  currency: varchar("currency", { length: 3 }).default("AUD").notNull(),
  balanceCents: bigint("balance_cents", { mode: "number" }).default(0).notNull(),
  availableBalanceCents: bigint("available_balance_cents", { mode: "number" }).default(0).notNull(),
  pendingBalanceCents: bigint("pending_balance_cents", { mode: "number" }).default(0).notNull(),
  status: walletStatusEnum("status").default("ACTIVE").notNull(),

  // Limits (in cents)
  dailyLimitCents: bigint("daily_limit_cents", { mode: "number" }),
  monthlyLimitCents: bigint("monthly_limit_cents", { mode: "number" }),
  maxBalanceCents: bigint("max_balance_cents", { mode: "number" }),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const walletsRelations = relations(wallets, ({ one, many }) => ({
  user: one(users, {
    fields: [wallets.userId],
    references: [users.id],
  }),
  cards: many(virtualCards),
  transactions: many(walletTransactions),
}));

// ============================================
// Wallet Transactions
// ============================================

export const walletTransactions = pgTable("wallet_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }).notNull(),

  type: varchar("type", { length: 20 }).notNull(), // CREDIT, DEBIT, HOLD, RELEASE
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  balanceBefore: bigint("balance_before", { mode: "number" }).notNull(),
  balanceAfter: bigint("balance_after", { mode: "number" }).notNull(),
  reference: varchar("reference", { length: 100 }),
  description: text("description"),

  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const walletTransactionsRelations = relations(walletTransactions, ({ one }) => ({
  wallet: one(wallets, {
    fields: [walletTransactions.walletId],
    references: [wallets.id],
  }),
}));

// ============================================
// Virtual Cards
// ============================================

export const virtualCards = pgTable("virtual_cards", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }).notNull(),
  productId: varchar("product_id").references(() => products.id, { onDelete: "set null" }),

  // Issuer Reference
  externalCardId: varchar("external_card_id", { length: 255 }).unique(),
  cardProgramId: varchar("card_program_id", { length: 255 }),

  // Card Details (last 4 only stored locally)
  lastFour: varchar("last_four", { length: 4 }),
  expMonth: integer("exp_month"),
  expYear: integer("exp_year"),
  cardBrand: varchar("card_brand", { length: 20 }).default("visa"),
  cardType: cardTypeEnum("card_type").default("VIRTUAL").notNull(),

  // Status
  status: cardStatusEnum("status").default("ACTIVE").notNull(),

  // Spending Controls (in cents)
  singleTxnLimitCents: bigint("single_txn_limit_cents", { mode: "number" }),
  dailyLimitCents: bigint("daily_limit_cents", { mode: "number" }),
  monthlyLimitCents: bigint("monthly_limit_cents", { mode: "number" }),

  // Merchant Controls
  allowedMccCodes: text("allowed_mcc_codes").array(),
  blockedMccCodes: text("blocked_mcc_codes").array(),
  allowedCountries: text("allowed_countries").array().default(sql`ARRAY['AU', 'NZ']`),

  // Metadata
  nickname: varchar("nickname", { length: 100 }),
  cardDesign: integer("card_design").default(1),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  frozenAt: timestamp("frozen_at"),
  cancelledAt: timestamp("cancelled_at"),
});

export const virtualCardsRelations = relations(virtualCards, ({ one, many }) => ({
  wallet: one(wallets, {
    fields: [virtualCards.walletId],
    references: [wallets.id],
  }),
  user: one(users, {
    fields: [virtualCards.userId],
    references: [users.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [virtualCards.businessUnitId],
    references: [businessUnits.id],
  }),
  product: one(products, {
    fields: [virtualCards.productId],
    references: [products.id],
  }),
  transactions: many(cardTransactions),
}));

// ============================================
// Card Transactions
// ============================================

export const cardTransactions = pgTable("card_transactions", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  cardId: varchar("card_id").references(() => virtualCards.id, { onDelete: "cascade" }).notNull(),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),

  // External References
  externalTxnId: varchar("external_txn_id", { length: 255 }).unique(),
  authorizationId: varchar("authorization_id", { length: 255 }),

  // Transaction Details
  type: txnTypeEnum("type").notNull(),
  status: txnStatusEnum("status").default("PENDING").notNull(),

  // Amounts (in cents)
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  currency: varchar("currency", { length: 3 }).default("AUD").notNull(),
  billingAmountCents: bigint("billing_amount_cents", { mode: "number" }),
  billingCurrency: varchar("billing_currency", { length: 3 }),
  fxRate: decimal("fx_rate", { precision: 10, scale: 6 }),

  // Merchant
  merchantName: varchar("merchant_name", { length: 255 }),
  merchantId: varchar("merchant_id", { length: 255 }),
  mcc: varchar("mcc", { length: 4 }),
  merchantCity: varchar("merchant_city", { length: 100 }),
  merchantCountry: varchar("merchant_country", { length: 2 }),

  // Timestamps
  authorizedAt: timestamp("authorized_at"),
  settledAt: timestamp("settled_at"),
  declinedReason: varchar("declined_reason", { length: 255 }),

  metadata: jsonb("metadata"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const cardTransactionsRelations = relations(cardTransactions, ({ one }) => ({
  card: one(virtualCards, {
    fields: [cardTransactions.cardId],
    references: [virtualCards.id],
  }),
  wallet: one(wallets, {
    fields: [cardTransactions.walletId],
    references: [wallets.id],
  }),
  user: one(users, {
    fields: [cardTransactions.userId],
    references: [users.id],
  }),
}));

// ============================================
// Fund Loads
// ============================================

export const fundLoads = pgTable("fund_loads", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }).notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),

  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  currency: varchar("currency", { length: 3 }).default("AUD").notNull(),
  sourceType: varchar("source_type", { length: 50 }).notNull(), // bank_transfer, card, manual
  sourceReference: varchar("source_reference", { length: 255 }),

  status: varchar("status", { length: 20 }).default("PENDING").notNull(),
  feeCents: bigint("fee_cents", { mode: "number" }).default(0).notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
});

export const fundLoadsRelations = relations(fundLoads, ({ one }) => ({
  wallet: one(wallets, {
    fields: [fundLoads.walletId],
    references: [wallets.id],
  }),
  user: one(users, {
    fields: [fundLoads.userId],
    references: [users.id],
  }),
}));

// ============================================
// Card Inventory
// ============================================

export const cardInventory = pgTable("card_inventory", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  productId: varchar("product_id").references(() => products.id, { onDelete: "cascade" }),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }).notNull(),

  // Inventory counts
  totalCards: integer("total_cards").default(0).notNull(),
  allocatedCards: integer("allocated_cards").default(0).notNull(),
  availableCards: integer("available_cards").default(0).notNull(),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const cardInventoryRelations = relations(cardInventory, ({ one, many }) => ({
  product: one(products, {
    fields: [cardInventory.productId],
    references: [products.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [cardInventory.businessUnitId],
    references: [businessUnits.id],
  }),
  organization: one(organizations, {
    fields: [cardInventory.organizationId],
    references: [organizations.id],
  }),
  shippingRecords: many(shippingRecords),
}));

// ============================================
// Shipping Records
// ============================================

export const shippingRecords = pgTable("shipping_records", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  inventoryId: varchar("inventory_id").references(() => cardInventory.id, { onDelete: "cascade" }).notNull(),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "cascade" }).notNull(),

  quantity: integer("quantity").notNull(),
  shippingAddress: text("shipping_address"),
  trackingNumber: varchar("tracking_number", { length: 100 }),
  carrier: varchar("carrier", { length: 50 }),
  status: shippingStatusEnum("status").default("PENDING").notNull(),

  shippedAt: timestamp("shipped_at"),
  deliveredAt: timestamp("delivered_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const shippingRecordsRelations = relations(shippingRecords, ({ one }) => ({
  inventory: one(cardInventory, {
    fields: [shippingRecords.inventoryId],
    references: [cardInventory.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [shippingRecords.businessUnitId],
    references: [businessUnits.id],
  }),
}));

// ============================================
// Support Tickets
// ============================================

export const supportTickets = pgTable("support_tickets", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),
  businessUnitId: varchar("business_unit_id").references(() => businessUnits.id, { onDelete: "set null" }),

  subject: text("subject").notNull(),
  description: text("description"),
  category: ticketCategoryEnum("category").default("OTHER").notNull(),
  priority: ticketPriorityEnum("priority").default("MEDIUM").notNull(),
  status: ticketStatusEnum("status").default("OPEN").notNull(),

  assignedTo: varchar("assigned_to").references(() => admins.id, { onDelete: "set null" }),
  resolvedAt: timestamp("resolved_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const supportTicketsRelations = relations(supportTickets, ({ one }) => ({
  user: one(users, {
    fields: [supportTickets.userId],
    references: [users.id],
  }),
  businessUnit: one(businessUnits, {
    fields: [supportTickets.businessUnitId],
    references: [businessUnits.id],
  }),
  assignedAdmin: one(admins, {
    fields: [supportTickets.assignedTo],
    references: [admins.id],
  }),
}));

// ============================================
// 3rd Party Integrations
// ============================================

export const integrations = pgTable("integrations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  organizationId: varchar("organization_id").references(() => organizations.id, { onDelete: "cascade" }),

  provider: integrationProviderEnum("provider").notNull(),
  type: integrationTypeEnum("type").notNull(),
  name: text("name").notNull(),

  // Credentials (should be encrypted in production)
  apiKeyEncrypted: text("api_key_encrypted"),
  apiSecretEncrypted: text("api_secret_encrypted"),
  clientId: text("client_id"),
  merchantId: text("merchant_id"),

  // Configuration
  environment: environmentEnum("environment").default("sandbox").notNull(),
  config: jsonb("config"),
  isActive: boolean("is_active").default(false).notNull(),

  // Testing
  lastTestedAt: timestamp("last_tested_at"),
  lastTestResult: varchar("last_test_result", { length: 20 }),

  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const integrationsRelations = relations(integrations, ({ one }) => ({
  organization: one(organizations, {
    fields: [integrations.organizationId],
    references: [organizations.id],
  }),
}));

// ============================================
// Audit Log
// ============================================

export const auditLog = pgTable("audit_log", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  timestamp: timestamp("timestamp").defaultNow().notNull(),

  // Unified user reference
  userId: varchar("user_id").references(() => users.id, { onDelete: "set null" }),

  // Denormalized for query performance (snapshot at time of action)
  userType: userTypeEnum("user_type"),
  userRole: roleEnum("user_role"),
  userEmail: text("user_email"),

  // Action details
  action: varchar("action", { length: 100 }).notNull(),
  resourceType: varchar("resource_type", { length: 50 }),
  resourceId: varchar("resource_id"),

  // Organizational context for scoped queries
  organizationId: varchar("organization_id"),
  businessUnitId: varchar("business_unit_id"),

  // Request context
  ipAddress: inet("ip_address"),
  userAgent: text("user_agent"),
  requestId: varchar("request_id"),

  // State tracking
  oldValues: jsonb("old_values"),
  newValues: jsonb("new_values"),

  // Flexible metadata
  metadata: jsonb("metadata"),
});

// ============================================
// Email Templates
// ============================================

export const emailTemplateTypeEnum = pgEnum("email_template_type", [
  "TRANSACTIONAL",
  "MARKETING",
  "NOTIFICATION",
  "SYSTEM",
]);

export const emailTemplates = pgTable("email_templates", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  // Template identification
  templateKey: varchar("template_key", { length: 100 }).notNull().unique(),
  name: text("name").notNull(),
  description: text("description"),
  category: emailTemplateTypeEnum("category").default("TRANSACTIONAL").notNull(),

  // Content
  subject: text("subject").notNull(),
  htmlContent: text("html_content").notNull(),
  textContent: text("text_content"),

  // Styling configuration
  styling: jsonb("styling").$type<{
    primaryColor?: string;
    secondaryColor?: string;
    logoUrl?: string;
    fontFamily?: string;
    headerBackground?: string;
    footerText?: string;
  }>(),

  // Variable definitions
  variables: jsonb("variables").$type<Array<{
    name: string;
    description: string;
    required: boolean;
    defaultValue?: string;
    example?: string;
  }>>(),

  // Status
  isActive: boolean("is_active").default(true).notNull(),
  isDefault: boolean("is_default").default(false).notNull(),

  // Metadata
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
  createdBy: varchar("created_by"),
  updatedBy: varchar("updated_by"),
});

export const insertEmailTemplateSchema = createInsertSchema(emailTemplates).omit({
  id: true,
  createdAt: true,
  updatedAt: true,
});

// Email send logs for tracking
export const emailLogs = pgTable("email_logs", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  templateId: varchar("template_id").references(() => emailTemplates.id, { onDelete: "set null" }),
  templateKey: varchar("template_key", { length: 100 }),

  // Recipient info
  recipientEmail: text("recipient_email").notNull(),
  recipientName: text("recipient_name"),

  // Content sent
  subject: text("subject").notNull(),

  // Status tracking
  status: varchar("status", { length: 20 }).notNull(), // queued, sent, delivered, failed, bounced
  errorMessage: text("error_message"),
  resendMessageId: varchar("resend_message_id", { length: 100 }),

  // Context
  customerId: varchar("customer_id"),
  metadata: jsonb("metadata"),

  // Timestamps
  sentAt: timestamp("sent_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const insertEmailLogSchema = createInsertSchema(emailLogs).omit({
  id: true,
  createdAt: true,
});

// ============================================
// PayHub QR Payments (India Phase 1)
// ============================================

export const payhubQrPayments = pgTable("payhub_qr_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => customers.id, { onDelete: "cascade" }).notNull(),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }),
  cardId: varchar("card_id").references(() => virtualCards.id, { onDelete: "set null" }),

  // PayHub references
  payhubQrId: varchar("payhub_qr_id", { length: 100 }).unique(),
  payhubTransactionId: varchar("payhub_transaction_id", { length: 100 }),

  // QR/Payment details
  amountPaise: bigint("amount_paise", { mode: "number" }).notNull(), // Amount in paise (INR)
  currency: varchar("currency", { length: 3 }).default("INR").notNull(),
  purpose: text("purpose"),
  qrCodeData: text("qr_code_data"), // Base64 or UPI string
  qrCodeUrl: text("qr_code_url"),
  upiString: text("upi_string"),

  // Status
  status: upiPaymentStatusEnum("status").default("PENDING").notNull(),
  payerVpa: varchar("payer_vpa", { length: 100 }),
  payerName: varchar("payer_name", { length: 200 }),
  paidAmountPaise: bigint("paid_amount_paise", { mode: "number" }),
  failureReason: text("failure_reason"),

  // Timestamps
  expiresAt: timestamp("expires_at"),
  scannedAt: timestamp("scanned_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),

  // Metadata for webhook processing
  webhookPayload: jsonb("webhook_payload"),
});

export const payhubQrPaymentsRelations = relations(payhubQrPayments, ({ one }) => ({
  customer: one(customers, {
    fields: [payhubQrPayments.customerId],
    references: [customers.id],
  }),
  wallet: one(wallets, {
    fields: [payhubQrPayments.walletId],
    references: [wallets.id],
  }),
  card: one(virtualCards, {
    fields: [payhubQrPayments.cardId],
    references: [virtualCards.id],
  }),
}));

// ============================================
// PayHub Top-Ups (India Phase 1)
// ============================================

export const payhubTopUps = pgTable("payhub_topups", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }).notNull(),

  // PayHub references
  payhubTopUpId: varchar("payhub_topup_id", { length: 100 }).unique(),
  payhubTransactionId: varchar("payhub_transaction_id", { length: 100 }),
  bankReferenceNumber: varchar("bank_reference_number", { length: 100 }),

  // Top-up details
  amountPaise: bigint("amount_paise", { mode: "number" }).notNull(),
  currency: varchar("currency", { length: 3 }).default("INR").notNull(),
  sourceType: varchar("source_type", { length: 20 }).notNull(), // UPI, NETBANKING, CARD, NEFT, RTGS
  paymentUrl: text("payment_url"),
  qrCodeData: text("qr_code_data"),
  upiString: text("upi_string"),

  // Status
  status: varchar("status", { length: 20 }).default("INITIATED").notNull(), // INITIATED, AWAITING_PAYMENT, PENDING, SUCCESS, FAILED, EXPIRED, REFUNDED
  paidAmountPaise: bigint("paid_amount_paise", { mode: "number" }),
  failureReason: text("failure_reason"),

  // Timestamps
  expiresAt: timestamp("expires_at"),
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),

  webhookPayload: jsonb("webhook_payload"),
});

export const payhubTopUpsRelations = relations(payhubTopUps, ({ one }) => ({
  user: one(users, {
    fields: [payhubTopUps.userId],
    references: [users.id],
  }),
  wallet: one(wallets, {
    fields: [payhubTopUps.walletId],
    references: [wallets.id],
  }),
}));

// ============================================
// Transaction Journey (Display Panel)
// ============================================

export const transactionJourneys = pgTable("transaction_journeys", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  // Reference to the actual transaction/operation
  referenceType: varchar("reference_type", { length: 50 }).notNull(), // QR_PAYMENT, TOPUP, KYC, CARD_ISSUE, WITHDRAWAL
  referenceId: varchar("reference_id").notNull(),
  userId: varchar("user_id").references(() => users.id, { onDelete: "cascade" }),

  // Journey details
  journeyType: varchar("journey_type", { length: 50 }).notNull(), // USER_ONBOARDING, PAYMENT_COLLECTION, CARD_ISSUANCE, FUND_LOAD, WITHDRAWAL
  currentStep: varchar("current_step", { length: 50 }).notNull(),
  totalSteps: integer("total_steps").default(1).notNull(),
  stepNumber: integer("step_number").default(1).notNull(),

  // Status
  status: journeyStatusEnum("status").default("INITIATED").notNull(),
  errorMessage: text("error_message"),
  errorCode: varchar("error_code", { length: 50 }),

  // Timing
  startedAt: timestamp("started_at").defaultNow().notNull(),
  lastActivityAt: timestamp("last_activity_at").defaultNow().notNull(),
  completedAt: timestamp("completed_at"),
  timeoutAt: timestamp("timeout_at"),

  // External system tracking
  upstreamProvider: varchar("upstream_provider", { length: 50 }), // PAYHUB
  upstreamRequestId: varchar("upstream_request_id", { length: 100 }),
  upstreamStatus: varchar("upstream_status", { length: 50 }),
  downstreamProvider: varchar("downstream_provider", { length: 50 }), // ISERVEU
  downstreamRequestId: varchar("downstream_request_id", { length: 100 }),
  downstreamStatus: varchar("downstream_status", { length: 50 }),

  // Full journey log
  journeyLog: jsonb("journey_log").$type<Array<{
    timestamp: string;
    step: string;
    status: string;
    message?: string;
    provider?: string;
    requestId?: string;
    response?: Record<string, unknown>;
  }>>().default([]),

  metadata: jsonb("metadata"),
});

export const transactionJourneysRelations = relations(transactionJourneys, ({ one }) => ({
  user: one(users, {
    fields: [transactionJourneys.userId],
    references: [users.id],
  }),
}));

// ============================================
// Host Server Operations Log (India Phase 1)
// ============================================

export const iserveuOperations = pgTable("iserveu_operations", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => customers.id, { onDelete: "cascade" }),
  cardId: varchar("card_id").references(() => virtualCards.id, { onDelete: "set null" }),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "set null" }),

  // Operation details
  operationType: iserveuOperationTypeEnum("operation_type").notNull(),
  iserveuRequestId: varchar("iserveu_request_id", { length: 100 }),
  iserveuReferenceId: varchar("iserveu_reference_id", { length: 100 }),

  // Request/Response
  requestPayload: jsonb("request_payload"),
  responsePayload: jsonb("response_payload"),

  // Status
  status: journeyStatusEnum("status").default("INITIATED").notNull(),
  errorMessage: text("error_message"),
  errorCode: varchar("error_code", { length: 50 }),

  // Timestamps
  requestedAt: timestamp("requested_at").defaultNow().notNull(),
  respondedAt: timestamp("responded_at"),
  completedAt: timestamp("completed_at"),
});

export const iserveuOperationsRelations = relations(iserveuOperations, ({ one }) => ({
  customer: one(customers, {
    fields: [iserveuOperations.customerId],
    references: [customers.id],
  }),
  card: one(virtualCards, {
    fields: [iserveuOperations.cardId],
    references: [virtualCards.id],
  }),
  wallet: one(wallets, {
    fields: [iserveuOperations.walletId],
    references: [wallets.id],
  }),
}));

// ============================================
// PayHub Webhook Events (India Phase 1)
// ============================================

export const payhubWebhookEvents = pgTable("payhub_webhook_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  // Event details
  eventId: varchar("event_id", { length: 100 }).unique().notNull(),
  eventType: payhubEventTypeEnum("event_type").notNull(),

  // References
  referenceId: varchar("reference_id", { length: 100 }), // qrId or topUpId
  transactionId: varchar("transaction_id", { length: 100 }),

  // Payload
  rawPayload: text("raw_payload").notNull(),
  parsedPayload: jsonb("parsed_payload"),
  signature: varchar("signature", { length: 255 }),
  signatureValid: boolean("signature_valid"),

  // Processing
  processed: boolean("processed").default(false).notNull(),
  processedAt: timestamp("processed_at"),
  processingError: text("processing_error"),
  retryCount: integer("retry_count").default(0).notNull(),

  receivedAt: timestamp("received_at").defaultNow().notNull(),
});

// ============================================
// Stripe Payments
// ============================================

export const stripePayments = pgTable("stripe_payments", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => customers.id, { onDelete: "cascade" }),
  walletId: varchar("wallet_id").references(() => wallets.id, { onDelete: "cascade" }),

  // Stripe references
  stripeSessionId: varchar("stripe_session_id", { length: 255 }).unique(),
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }).unique(),
  stripeChargeId: varchar("stripe_charge_id", { length: 255 }),

  // Payment details
  amountCents: bigint("amount_cents", { mode: "number" }).notNull(),
  currency: varchar("currency", { length: 3 }).default("usd").notNull(),
  description: text("description"),

  // Status
  status: varchar("status", { length: 30 }).default("PENDING").notNull(),
  failureReason: text("failure_reason"),
  failureCode: varchar("failure_code", { length: 100 }),

  // Refund tracking
  refundedAmountCents: bigint("refunded_amount_cents", { mode: "number" }).default(0),

  // URLs (for checkout sessions)
  successUrl: text("success_url"),
  cancelUrl: text("cancel_url"),
  checkoutUrl: text("checkout_url"),

  // Metadata
  metadata: jsonb("metadata"),

  // Timestamps
  completedAt: timestamp("completed_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const stripePaymentsRelations = relations(stripePayments, ({ one }) => ({
  customer: one(customers, {
    fields: [stripePayments.customerId],
    references: [customers.id],
  }),
  wallet: one(wallets, {
    fields: [stripePayments.walletId],
    references: [wallets.id],
  }),
}));

// ============================================
// Stripe Webhook Events (Idempotency)
// ============================================

export const stripeWebhookEvents = pgTable("stripe_webhook_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),

  // Stripe event details
  stripeEventId: varchar("stripe_event_id", { length: 255 }).unique().notNull(),
  eventType: varchar("event_type", { length: 100 }).notNull(),

  // References
  stripePaymentIntentId: varchar("stripe_payment_intent_id", { length: 255 }),
  stripeSessionId: varchar("stripe_session_id", { length: 255 }),

  // Payload
  rawPayload: text("raw_payload").notNull(),
  parsedPayload: jsonb("parsed_payload"),

  // Processing
  processed: boolean("processed").default(false).notNull(),
  processedAt: timestamp("processed_at"),
  processingError: text("processing_error"),
  retryCount: integer("retry_count").default(0).notNull(),

  receivedAt: timestamp("received_at").defaultNow().notNull(),
});

// ============================================
// Insert Schemas (Zod validation)
// ============================================

export const insertOrganizationSchema = createInsertSchema(organizations).omit({ id: true, createdAt: true, updatedAt: true });
export const insertProductSchema = createInsertSchema(products).omit({ id: true, createdAt: true, updatedAt: true });
export const insertBusinessUnitSchema = createInsertSchema(businessUnits).omit({ id: true, createdAt: true, updatedAt: true });
export const insertAdminSchema = createInsertSchema(admins).omit({ id: true, createdAt: true });
export const insertCustomerSchema = createInsertSchema(customers).omit({ id: true, createdAt: true, updatedAt: true });
export const insertUserSchema = createInsertSchema(users).omit({ id: true, createdAt: true, updatedAt: true });
export const insertKycRecordSchema = createInsertSchema(kycRecords).omit({ id: true, createdAt: true });
export const insertWalletSchema = createInsertSchema(wallets).omit({ id: true, createdAt: true, updatedAt: true });
export const insertWalletTransactionSchema = createInsertSchema(walletTransactions).omit({ id: true, createdAt: true });
export const insertVirtualCardSchema = createInsertSchema(virtualCards).omit({ id: true, createdAt: true, updatedAt: true });
export const insertCardTransactionSchema = createInsertSchema(cardTransactions).omit({ id: true, createdAt: true, updatedAt: true });
export const insertFundLoadSchema = createInsertSchema(fundLoads).omit({ id: true, createdAt: true });
export const insertCardInventorySchema = createInsertSchema(cardInventory).omit({ id: true, createdAt: true, updatedAt: true });
export const insertShippingRecordSchema = createInsertSchema(shippingRecords).omit({ id: true, createdAt: true });

export const insertSupportTicketSchema = createInsertSchema(supportTickets).omit({ id: true, createdAt: true, updatedAt: true });
export const insertIntegrationSchema = createInsertSchema(integrations).omit({ id: true, createdAt: true, updatedAt: true });

// India Phase 1 insert schemas
export const insertPayhubQrPaymentSchema = createInsertSchema(payhubQrPayments).omit({ id: true, createdAt: true, updatedAt: true });
export const insertPayhubTopUpSchema = createInsertSchema(payhubTopUps).omit({ id: true, createdAt: true, updatedAt: true });
export const insertTransactionJourneySchema = createInsertSchema(transactionJourneys).omit({ id: true, startedAt: true, lastActivityAt: true });
export const insertIserveuOperationSchema = createInsertSchema(iserveuOperations).omit({ id: true, requestedAt: true });
export const insertPayhubWebhookEventSchema = createInsertSchema(payhubWebhookEvents).omit({ id: true, receivedAt: true });
export const insertAuditLogSchema = createInsertSchema(auditLog).omit({ id: true, timestamp: true });

// Stripe insert schemas
export const insertStripePaymentSchema = createInsertSchema(stripePayments).omit({ id: true, createdAt: true, updatedAt: true });
export const insertStripeWebhookEventSchema = createInsertSchema(stripeWebhookEvents).omit({ id: true, receivedAt: true });

// ============================================
// Type Exports
// ============================================

export type Organization = typeof organizations.$inferSelect;
export type InsertOrganization = z.infer<typeof insertOrganizationSchema>;
export type Product = typeof products.$inferSelect;
export type InsertProduct = z.infer<typeof insertProductSchema>;
export type BusinessUnit = typeof businessUnits.$inferSelect;
export type InsertBusinessUnit = z.infer<typeof insertBusinessUnitSchema>;
export type Admin = typeof admins.$inferSelect;
export type InsertAdmin = z.infer<typeof insertAdminSchema>;
export type Customer = typeof customers.$inferSelect;
export type InsertCustomer = z.infer<typeof insertCustomerSchema>;
export type User = typeof users.$inferSelect;
export type InsertUser = z.infer<typeof insertUserSchema>;
export type KycRecord = typeof kycRecords.$inferSelect;
export type InsertKycRecord = z.infer<typeof insertKycRecordSchema>;
export type Wallet = typeof wallets.$inferSelect;
export type InsertWallet = z.infer<typeof insertWalletSchema>;
export type WalletTransaction = typeof walletTransactions.$inferSelect;
export type InsertWalletTransaction = z.infer<typeof insertWalletTransactionSchema>;
export type VirtualCard = typeof virtualCards.$inferSelect;
export type InsertVirtualCard = z.infer<typeof insertVirtualCardSchema>;
export type CardTransaction = typeof cardTransactions.$inferSelect;
export type InsertCardTransaction = z.infer<typeof insertCardTransactionSchema>;
export type FundLoad = typeof fundLoads.$inferSelect;
export type InsertFundLoad = z.infer<typeof insertFundLoadSchema>;
export type CardInventory = typeof cardInventory.$inferSelect;
export type InsertCardInventory = z.infer<typeof insertCardInventorySchema>;
export type ShippingRecord = typeof shippingRecords.$inferSelect;
export type InsertShippingRecord = z.infer<typeof insertShippingRecordSchema>;
export type SupportTicket = typeof supportTickets.$inferSelect;
export type InsertSupportTicket = z.infer<typeof insertSupportTicketSchema>;
export type Integration = typeof integrations.$inferSelect;
export type InsertIntegration = z.infer<typeof insertIntegrationSchema>;
export type AuditLog = typeof auditLog.$inferSelect;
export type InsertAuditLog = z.infer<typeof insertAuditLogSchema>;
export type EmailTemplate = typeof emailTemplates.$inferSelect;
export type InsertEmailTemplate = z.infer<typeof insertEmailTemplateSchema>;
export type EmailLog = typeof emailLogs.$inferSelect;
export type InsertEmailLog = z.infer<typeof insertEmailLogSchema>;
export type EmailTemplateType = typeof emailTemplateTypeEnum.enumValues[number];

// India Phase 1 type exports
export type PayhubQrPayment = typeof payhubQrPayments.$inferSelect;
export type InsertPayhubQrPayment = z.infer<typeof insertPayhubQrPaymentSchema>;
export type PayhubTopUp = typeof payhubTopUps.$inferSelect;
export type InsertPayhubTopUp = z.infer<typeof insertPayhubTopUpSchema>;
export type TransactionJourney = typeof transactionJourneys.$inferSelect;
export type InsertTransactionJourney = z.infer<typeof insertTransactionJourneySchema>;
export type IserveuOperation = typeof iserveuOperations.$inferSelect;
export type InsertIserveuOperation = z.infer<typeof insertIserveuOperationSchema>;
export type PayhubWebhookEvent = typeof payhubWebhookEvents.$inferSelect;
export type InsertPayhubWebhookEvent = z.infer<typeof insertPayhubWebhookEventSchema>;

// Stripe type exports
export type StripePayment = typeof stripePayments.$inferSelect;
export type InsertStripePayment = z.infer<typeof insertStripePaymentSchema>;
export type StripeWebhookEvent = typeof stripeWebhookEvents.$inferSelect;
export type InsertStripeWebhookEvent = z.infer<typeof insertStripeWebhookEventSchema>;

// Enum type exports
export type UserType = typeof userTypeEnum.enumValues[number];
export type Role = typeof roleEnum.enumValues[number];
export type Status = typeof statusEnum.enumValues[number];
export type RequestStatus = typeof requestStatusEnum.enumValues[number];
export type KycStatus = typeof kycStatusEnum.enumValues[number];
export type CardStatus = typeof cardStatusEnum.enumValues[number];
export type CardType = typeof cardTypeEnum.enumValues[number];
export type CardNetwork = typeof cardNetworkEnum.enumValues[number];
export type TxnType = typeof txnTypeEnum.enumValues[number];
export type TxnStatus = typeof txnStatusEnum.enumValues[number];
export type WalletStatus = typeof walletStatusEnum.enumValues[number];
export type ShippingStatus = typeof shippingStatusEnum.enumValues[number];
export type TicketStatus = typeof ticketStatusEnum.enumValues[number];
export type TicketPriority = typeof ticketPriorityEnum.enumValues[number];
export type TicketCategory = typeof ticketCategoryEnum.enumValues[number];
export type IntegrationProvider = typeof integrationProviderEnum.enumValues[number];
export type IntegrationType = typeof integrationTypeEnum.enumValues[number];
export type Environment = typeof environmentEnum.enumValues[number];

// India Phase 1 enum type exports
export type AadhaarKycStatus = typeof aadhaarKycStatusEnum.enumValues[number];
export type UpiPaymentStatus = typeof upiPaymentStatusEnum.enumValues[number];
export type PayhubEventType = typeof payhubEventTypeEnum.enumValues[number];
export type IserveuOperationType = typeof iserveuOperationTypeEnum.enumValues[number];
export type JourneyStatus = typeof journeyStatusEnum.enumValues[number];

// ============================================
// Customer Portal (Customer-Facing Features)
// ============================================

// Customer Sessions Table
export const customerSessions = pgTable("customer_sessions", {
  sid: varchar("sid").primaryKey(),
  sess: jsonb("sess").notNull(),
  expire: timestamp("expire").notNull(),
});

// OTP Requests Table (for customer phone verification)
export const otpRequests = pgTable("otp_requests", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  phone: varchar("phone", { length: 20 }).notNull(),
  customerId: varchar("customer_id").references(() => customers.id, { onDelete: "cascade" }),
  otpCode: varchar("otp_code", { length: 6 }).notNull(),
  purpose: varchar("purpose", { length: 50 }).notNull(), // "signin", "profile_update", "security"
  verified: boolean("verified").default(false).notNull(),
  expiresAt: timestamp("expires_at").notNull(),
  verifiedAt: timestamp("verified_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const otpRequestsRelations = relations(otpRequests, ({ one }) => ({
  customer: one(customers, {
    fields: [otpRequests.customerId],
    references: [customers.id],
  }),
}));

export const insertOtpRequestSchema = createInsertSchema(otpRequests);
export type OtpRequest = typeof otpRequests.$inferSelect;
export type InsertOtpRequest = z.infer<typeof insertOtpRequestSchema>;

// ============================================
// Customer Access Codes (admin-issued portal login backdoor)
// ============================================

export const customerAccessCodes = pgTable("customer_access_codes", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  customerId: varchar("customer_id").references(() => users.id, { onDelete: "cascade" }).notNull(),
  codeHash: text("code_hash").notNull(),
  issuedByAdminId: varchar("issued_by_admin_id").references(() => users.id, { onDelete: "set null" }),
  expiresAt: timestamp("expires_at").notNull(),
  usedAt: timestamp("used_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export type CustomerAccessCode = typeof customerAccessCodes.$inferSelect;
export type InsertCustomerAccessCode = typeof customerAccessCodes.$inferInsert;

// ============================================
// Sync System
// ============================================

export const syncEventStatusEnum = pgEnum("sync_event_status", ["pending", "delivered", "failed", "expired"]);
export const syncActionEnum = pgEnum("sync_action", ["created", "updated", "deleted"]);

export const syncConfig = pgTable("sync_config", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  systemName: varchar("system_name", { length: 50 }).notNull().unique(),
  webhookUrl: text("webhook_url").notNull(),
  apiSecret: text("api_secret").notNull(),
  isActive: boolean("is_active").default(true).notNull(),
  lastHeartbeat: timestamp("last_heartbeat"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
  updatedAt: timestamp("updated_at").defaultNow().notNull(),
});

export const syncEvents = pgTable("sync_events", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventType: varchar("event_type", { length: 80 }).notNull(),
  entityType: varchar("entity_type", { length: 50 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  action: syncActionEnum("action").notNull(),
  payload: jsonb("payload").notNull(),
  changedFields: jsonb("changed_fields").$type<string[]>(),
  originSystem: varchar("origin_system", { length: 50 }).notNull(),
  status: syncEventStatusEnum("status").default("pending").notNull(),
  attempts: integer("attempts").default(0).notNull(),
  maxAttempts: integer("max_attempts").default(5).notNull(),
  nextRetryAt: timestamp("next_retry_at"),
  lastError: text("last_error"),
  deliveredAt: timestamp("delivered_at"),
  createdAt: timestamp("created_at").defaultNow().notNull(),
});

export const syncLedger = pgTable("sync_ledger", {
  id: varchar("id").primaryKey().default(sql`gen_random_uuid()`),
  eventId: varchar("event_id").notNull().unique(),
  entityType: varchar("entity_type", { length: 50 }).notNull(),
  entityId: varchar("entity_id").notNull(),
  action: syncActionEnum("action").notNull(),
  originSystem: varchar("origin_system", { length: 50 }).notNull(),
  appliedAt: timestamp("applied_at").defaultNow().notNull(),
  payloadHash: varchar("payload_hash", { length: 64 }),
});

export const insertSyncConfigSchema = createInsertSchema(syncConfig).omit({ id: true, createdAt: true, updatedAt: true });
export type SyncConfig = typeof syncConfig.$inferSelect;
export type InsertSyncConfig = z.infer<typeof insertSyncConfigSchema>;

export const insertSyncEventSchema = createInsertSchema(syncEvents).omit({ id: true, createdAt: true });
export type SyncEvent = typeof syncEvents.$inferSelect;
export type InsertSyncEvent = z.infer<typeof insertSyncEventSchema>;

export const insertSyncLedgerSchema = createInsertSchema(syncLedger).omit({ id: true, appliedAt: true });
export type SyncLedgerEntry = typeof syncLedger.$inferSelect;
export type InsertSyncLedgerEntry = z.infer<typeof insertSyncLedgerSchema>;

// ============================================
// Login Schema
// ============================================

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(6, "Password must be at least 6 characters"),
});

export type LoginInput = z.infer<typeof loginSchema>;

// ============================================
// Country & Currency Constants
// ============================================

export const COUNTRY_CURRENCY_MAP: Record<BaseCountry, BaseCurrency[]> = {
  AU: ['AUD'],
  NZ: ['NZD'],
  IN: ['INR'],
  GB: ['GBP', 'EUR'],
  US: ['USD'],
};

export const COUNTRY_LABELS: Record<BaseCountry, string> = {
  AU: 'Australia',
  NZ: 'New Zealand',
  IN: 'India',
  GB: 'United Kingdom',
  US: 'United States',
};

export const CURRENCY_LOCALE_MAP: Record<BaseCurrency, string> = {
  AUD: 'en-AU',
  NZD: 'en-NZ',
  INR: 'en-IN',
  GBP: 'en-GB',
  EUR: 'en-GB',
  USD: 'en-US',
};
